import { useEffect, useRef } from 'react';

const WHATSAPP_LINK = 'https://wa.me/573057096663?text=Hola%2C%20%C2%BFEn%20qu%C3%A9%20podemos%20ayudarte%3F';

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const scrollY = window.scrollY;
        heroRef.current.style.backgroundPositionY = `${scrollY * 0.4}px`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url('https://readdy.ai/api/search-image?query=high%20performance%20car%20engine%20close%20up%20mechanical%20parts%20pistons%20cylinders%20dark%20dramatic%20lighting%20professional%20automotive%20photography%20deep%20black%20background%20metallic%20chrome%20details%20industrial%20aesthetic%20ultra%20detailed%20cinematic&width=1920&height=1080&seq=hero001&orientation=landscape')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/40"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-32 flex flex-col lg:flex-row items-center gap-8 md:gap-12">
        {/* Left Content */}
        <div className="flex-1 w-full">
          {/* Logo destacado */}
          <div className="flex items-center gap-4 md:gap-5 mb-5 md:mb-7">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#2563eb]/30 blur-xl scale-125"></div>
              <img
                src="https://static.readdy.ai/image/1c644e56cddf516eacde8781ded33937/e9560f414d66cc985767c3e7d3e0dff0.jpeg"
                alt="Chevrokee Automotriz"
                className="relative w-16 h-16 md:w-24 md:h-24 rounded-full object-cover border-2 border-[#2563eb]/60 shadow-2xl shadow-blue-900/50"
              />
            </div>
            <div>
              <div className="text-white font-extrabold text-lg md:text-2xl leading-tight tracking-wide">
                Chevrokee
              </div>
              <div className="text-[#2563eb] font-bold text-sm md:text-lg tracking-widest uppercase">
                Automotriz
              </div>
              <div className="text-gray-500 text-xs tracking-widest uppercase mt-0.5">Bogotá · Colombia</div>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 border border-[#2563eb]/60 text-[#2563eb] text-xs font-semibold uppercase tracking-widest px-3 py-1.5 md:px-4 md:py-2 rounded-full mb-4 md:mb-6">
            <i className="ri-map-pin-line"></i>
            Bogotá · Sector 7 de Agosto
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight mb-3 md:mb-4">
            Repuestos Mecánicos<br />
            <span className="text-[#2563eb]">Chevrolet · Suzuki · Jeep</span><br />
            <span className="text-gray-200">RAM · Dodge · Isuzu</span>
          </h1>

          <p className="text-gray-300 text-sm md:text-base mb-2 md:mb-3 leading-relaxed max-w-xl font-medium">
            Especialistas en repuestos mecánicos para Chevrolet, Suzuki, Jeep, RAM, Dodge e Isuzu en Bogotá. Motor, suspensión, frenos, transmisión y más — disponibilidad inmediata y asesoría técnica real.
          </p>

          <p className="text-[#2563eb] text-sm md:text-base mb-6 md:mb-8 font-semibold">
            <i className="ri-whatsapp-line mr-1"></i>
            Escríbenos ahora y te cotizamos en minutos
          </p>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-bold px-6 md:px-8 py-3.5 md:py-4 rounded-full text-sm md:text-base transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-blue-900/40"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Cotizar por WhatsApp
            </a>
            <a
              href="#marcas"
              className="flex items-center justify-center gap-2 border border-white/40 hover:border-white text-white font-semibold px-6 md:px-8 py-3.5 md:py-4 rounded-full text-sm md:text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Ver por Marca
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>

        {/* Right — Escudo + Stats Cards */}
        <div className="flex-shrink-0 flex flex-col items-center gap-4 md:gap-5 w-full lg:w-auto lg:max-w-xs">
          {/* Marcas badge grid */}
          <div className="w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 md:p-5">
            <p className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3 md:mb-4 text-center">Marcas disponibles</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { name: 'Chevrolet', href: '#chevrolet', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
                { name: 'Suzuki',    href: '#suzuki',    color: 'text-red-400',  bg: 'bg-red-500/10',  border: 'border-red-500/20' },
                { name: 'Jeep',      href: '#jeep',      color: 'text-green-400',bg: 'bg-green-500/10',border: 'border-green-500/20' },
                { name: 'RAM',       href: '#ram',       color: 'text-slate-300',bg: 'bg-slate-500/10',border: 'border-slate-500/20' },
                { name: 'Dodge',     href: '#dodge',     color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
                { name: 'Isuzu',     href: '#isuzu',     color: 'text-amber-400',bg: 'bg-amber-500/10',border: 'border-amber-500/20' },
              ].map((m) => (
                <a key={m.name} href={m.href} className={`flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-2.5 md:py-3 border ${m.bg} ${m.border} hover:scale-105 transition-transform duration-200 cursor-pointer`}>
                  <i className={`ri-car-line text-base md:text-lg ${m.color}`}></i>
                  <span className={`text-[10px] md:text-xs font-bold ${m.color} whitespace-nowrap`}>{m.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Stats Cards */}
          {[
            { icon: 'ri-tools-line',    value: '500+',      label: 'Repuestos Disponibles' },
            { icon: 'ri-map-pin-line',  value: 'Bogotá',    label: 'Sector 7 de Agosto' },
            { icon: 'ri-whatsapp-line', value: 'Inmediata', label: 'Respuesta por WhatsApp' },
            { icon: 'ri-car-line',      value: '6 Marcas',  label: 'Chevrolet · Suzuki · Jeep · RAM · Dodge · Isuzu' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 md:gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-4 md:px-5 py-3 md:py-4 w-full"
            >
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-[#2563eb]/15 rounded-lg flex-shrink-0">
                <i className={`${stat.icon} text-lg md:text-xl text-[#2563eb]`}></i>
              </div>
              <div className="min-w-0">
                <div className="text-white font-bold text-sm md:text-base">{stat.value}</div>
                <div className="text-gray-400 text-xs truncate">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>


    </section>
  );
}
